import { useEffect, useMemo, useState } from 'react';
import { MeasurementMarkerDto, SessionKindGroupName } from 'types/models/SessionsPageData';
import ChartHint from './ChartHint';
import { t } from 'utilities/TranslationUtils';
import { darkTooltip, KICK_RESULT_BUCKET_COLORS, isBinaryOnTargetKind, measurementResultBucket } from 'utilities/chartUtils';
import {
  TrendGroupBy,
  TREND_GROUP_OPTIONS as ALL_TREND_GROUP_OPTIONS,
  ResultBreakdownMetricKey,
  ResultBucketKey,
  ResultBreakdownMetricDef as MetricDef,
  buildBuckets,
  buildTrendData,
} from './playerPerformanceUtils';
import GrowChart from './GrowChart';

type ViewMode = 'groups' | 'trends';

const TREND_GROUP_OPTIONS = ALL_TREND_GROUP_OPTIONS.slice(0, 3);

const METRICS: MetricDef[] = [
  { key: 'ballSpeed',     labelKey: 'playerPerformance.metric_ballSpeedAvg',     unit: 'km/h', step: 5,    decimals: 0 },
  { key: 'distance',      labelKey: 'playerPerformance.metric_distanceAvg',      unit: 'm',    step: 1,    decimals: 0, hardStep: true },
  { key: 'hangTime',      labelKey: 'playerPerformance.metric_hangTimeAvg',      unit: 's',    step: 0.25, decimals: 2 },
  { key: 'gFactor',       labelKey: 'playerPerformance.metric_gFactor',          unit: '',     step: 0.25, decimals: 2 },
  { key: 'spinRate',      labelKey: 'playerPerformance.metric_spinRate',          unit: 'rpm',  step: 50,   decimals: 0 },
  { key: 'launchAngle',   labelKey: 'playerPerformance.metric_launchAngle',      unit: '°',    step: 5,    decimals: 0 },
  { key: 'pullDirection', labelKey: 'playerPerformance.metric_pullDirection',    unit: '°',    step: 10,   decimals: 0, hardStep: true },
  {
    key: 'maxHeight',
    labelKey: 'playerPerformance.metric_maxHeight',
    unit: 'm',
    step: 0.5,
    decimals: 1,
    customThresholds: [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7],
  },
  {
    key: 'wallCrossingHeight',
    labelKey: 'playerPerformance.metric_wallCrossingHeight',
    unit: 'm',
    step: 0.2,
    decimals: 2,
    hardStep: true,
  },
];

// Chip display order: best result first; also controls stacking order (goal = bottom)
const RESULT_SERIES_BASE: ReadonlyArray<{ key: ResultBucketKey; labelKey: string }> = [
  { key: 'goal',      labelKey: 'sessions.kickResult_Goal' },
  { key: 'onTarget',  labelKey: 'sessions.kickResult_OnTarget' },
  { key: 'offTarget', labelKey: 'sessions.kickResult_OffTarget' },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  measurements: MeasurementMarkerDto[];
  kind?: SessionKindGroupName;
}

const ResultBreakdownChart: React.FunctionComponent<Props> = ({ measurements, kind }) => {
  const isBinary = kind != null && isBinaryOnTargetKind(kind);
  const onTargetResultKinds = useMemo(
    () => measurements
      .filter((m) => measurementResultBucket(m, kind ?? null) === 'onTarget')
      .map((m) => (m.result ?? '').toLowerCase()),
    [measurements, kind],
  );
  const onTargetResultKindSet = useMemo(
    () => new Set(onTargetResultKinds),
    [onTargetResultKinds],
  );
  const onlySaved = onTargetResultKindSet.size === 1 && onTargetResultKindSet.has('saved');
  const onlySavable = onTargetResultKindSet.size === 1 && onTargetResultKindSet.has('savable');
  const allSavedLike = onTargetResultKindSet.size > 0 && Array.from(onTargetResultKindSet).every((v) => v === 'saved' || v === 'savable');
  const onTargetLabelKey = onlySaved
    ? 'sessions.kickResult_Saved'
    : onlySavable
    ? 'sessions.kickResult_Savable'
    : allSavedLike
    ? 'sessions.kickResult_Saved'
    : 'sessions.kickResult_OnTarget';
  const resultSeries = useMemo(
    () => RESULT_SERIES_BASE.map((r) => ({
      ...r,
      labelKey: r.key === 'onTarget' ? onTargetLabelKey : r.labelKey,
      color: r.key === 'goal'
        ? KICK_RESULT_BUCKET_COLORS.goal
        : r.key === 'onTarget'
        ? KICK_RESULT_BUCKET_COLORS.onTarget
        : KICK_RESULT_BUCKET_COLORS.offTarget,
    })),
    [onTargetLabelKey],
  );

  const [selectedMetric, setSelectedMetric] = useState<ResultBreakdownMetricKey>('ballSpeed');
  const [hiddenResults, setHiddenResults] = useState<Set<ResultBucketKey>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('groups');
  const [trendGroupBy, setTrendGroupBy] = useState<TrendGroupBy>('sessions');

  const effectiveHidden = useMemo<Set<ResultBucketKey>>(() => {
    if (!isBinary) return hiddenResults;
    const s = new Set(hiddenResults);
    s.add('goal');
    return s;
  }, [hiddenResults, isBinary]);

  // Distance is meaningless for penalties (fixed spot) — hide that metric for penalty sessions.
  const metrics = useMemo(
    () => METRICS.filter((m) => !(kind === 'Penalty' && m.key === 'distance')),
    [kind],
  );

  const metricsWithData = useMemo(
    () => new Set(metrics.filter((m) => buildBuckets(measurements, m).length > 0).map((m) => m.key)),
    [measurements, metrics],
  );

  useEffect(() => {
    const selectedExists = metrics.some((m) => m.key === selectedMetric);
    const selectedHasData = metricsWithData.has(selectedMetric);
    if (selectedExists && selectedHasData) return;

    const fallback = metrics.find((m) => metricsWithData.has(m.key)) ?? metrics[0];
    if (fallback && fallback.key !== selectedMetric) {
      setSelectedMetric(fallback.key);
    }
  }, [metrics, metricsWithData, selectedMetric]);

  const metricDef = useMemo(
    () => metrics.find((m) => m.key === selectedMetric) ?? metrics[0],
    [metrics, selectedMetric],
  );

  const buckets = useMemo(
    () => buildBuckets(measurements, metricDef),
    [measurements, metricDef],
  );

  const trendData = useMemo(
    () => buildTrendData(measurements, metricDef.key, trendGroupBy),
    [measurements, metricDef, trendGroupBy],
  );

  const toggleResult = (key: ResultBucketKey) => {
    setHiddenResults((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const groupsOption = useMemo(() => {
    // The unit moves to the axis name, so labels stay short ("18–19") and need no rotation even with many buckets.
    const unitSuffix = metricDef.unit ? ` ${metricDef.unit}` : '';
    const categories = buckets.map((b) => (unitSuffix && b.label.endsWith(unitSuffix) ? b.label.slice(0, -unitSuffix.length) : b.label));
    const visibleResultSeries = resultSeries.filter((r) => !effectiveHidden.has(r.key));
    const topSegmentByBucket = buckets.map((b) => {
      for (let i = visibleResultSeries.length - 1; i >= 0; i--) {
        const key = visibleResultSeries[i].key;
        if ((b[key] ?? 0) > 0) return key;
      }
      return null;
    });
    const maxY = Math.max(
      ...buckets.map((b) =>
        (effectiveHidden.has('offTarget') ? 0 : b.offTarget) +
        (effectiveHidden.has('onTarget') ? 0 : b.onTarget) +
        (effectiveHidden.has('goal') ? 0 : b.goal),
      ),
      1,
    );
    const manyBuckets = categories.length > 16;

    return {
      tooltip: {
        ...darkTooltip('axis'),
        axisPointer: { type: 'shadow' as const },
        formatter: (params: any[]) => {
          if (!params?.length) return '';
          const b = buckets[params[0].dataIndex];
          if (!b) return '';
          const total = b.goal + b.onTarget + b.offTarget;
          const lines: string[] = [`<b>${b.label}</b>`];
          if (!isBinary) lines.push(`${t('playerPerformance.resultBreakdown_goal')}: <b>${b.goal}</b>`);
          lines.push(
            `${t(onTargetLabelKey)}: <b>${b.onTarget}</b>`,
            `${t('playerPerformance.resultBreakdown_offTarget')}: <b>${b.offTarget}</b>`,
            `<span style="opacity:0.6">${t('playerPerformance.resultBreakdown_total')}: ${total}</span>`,
          );
          return lines.join('<br/>');
        },
      },
      grid: { top: 12, bottom: 12, left: 8, right: 30, containLabel: true }, // right: room for the axis unit
      xAxis: {
        type: 'category' as const,
        data: categories,
        name: metricDef.unit,
        nameLocation: 'end' as const,
        nameGap: 6,
        nameTextStyle: { fontSize: 10, color: '#6B7280', align: 'left' as const },
        axisLabel: { fontSize: 10, color: '#374151', rotate: 0, interval: manyBuckets ? ('auto' as const) : 0, hideOverlap: true },
        axisLine: { lineStyle: { color: '#E5E7EB' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value' as const,
        axisLabel: {
          fontSize: 11,
          color: '#374151',
          formatter: (v: number) => (Number.isInteger(v) ? String(v) : ''),
        },
        splitLine: { lineStyle: { color: '#E5E7EB', opacity: 0.5 } },
        axisLine: { show: false },
        axisTick: { show: false },
        max: Math.ceil(maxY * 1.2),
        minInterval: 1,
      },
      series: resultSeries.map((r) => ({
        name: t(r.labelKey),
        type: 'bar' as const,
        stack: 'results',
        data: effectiveHidden.has(r.key)
          ? buckets.map(() => 0)
          : buckets.map((b, idx) => {
              const value = b[r.key];
              const isTopSegment = topSegmentByBucket[idx] === r.key && value > 0;
              return {
                value,
                itemStyle: {
                  color: r.color,
                  borderRadius: isTopSegment
                    ? ([8, 8, 0, 0] as [number, number, number, number])
                    : ([0, 0, 0, 0] as [number, number, number, number]),
                },
              };
            }),
        barMaxWidth: 52,
        emphasis: { focus: 'none' as const },
      })),
    };
  }, [buckets, effectiveHidden, isBinary, metricDef, onTargetLabelKey, resultSeries]);

  const trendsOption = useMemo(() => {
    const unitSuffix = metricDef.unit ? ` ${metricDef.unit}` : '';
    const xLabels = trendData.map((s) => s.label);
    const rotateLabels = xLabels.length > 6;

    return {
      tooltip: {
        ...darkTooltip('axis'),
        formatter: (params: any[]) => {
          if (!params?.length) return '';
          const sess = trendData[params[0].dataIndex];
          if (!sess) return '';
          const lines: string[] = [`<b>${sess.label}</b>`];
          for (const r of resultSeries) {
            if (effectiveHidden.has(r.key)) continue;
            const acc = sess.buckets[r.key];
            if (acc.count > 0) {
              const avg = (acc.sum / acc.count).toFixed(metricDef.decimals);
              lines.push(`<span style="color:${r.color}">●</span> ${t(r.labelKey)}: <b>${avg}${unitSuffix}</b> (${acc.count})`);
            }
          }
          return lines.join('<br/>');
        },
      },
      grid: { top: 12, bottom: rotateLabels ? 60 : 36, left: 48, right: 16, containLabel: false },
      xAxis: {
        type: 'category' as const,
        data: xLabels,
        axisLabel: { fontSize: 10, color: '#374151', rotate: rotateLabels ? 30 : 0 },
        axisLine: { lineStyle: { color: '#E5E7EB' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value' as const,
        axisLabel: {
          fontSize: 10,
          color: '#9CA3AF',
          formatter: (v: number) => `${parseFloat(v.toFixed(metricDef.decimals))}${unitSuffix}`,
        },
        splitLine: { lineStyle: { color: '#E5E7EB', opacity: 0.5 } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: resultSeries
        .filter((r) => !effectiveHidden.has(r.key))
        .map((r) => ({
          name: t(r.labelKey),
          type: 'line' as const,
          data: trendData.map((s) => {
            const acc = s.buckets[r.key];
            return acc.count > 0 ? parseFloat((acc.sum / acc.count).toFixed(metricDef.decimals + 2)) : null;
          }),
          smooth: false,
          symbol: 'circle',
          symbolSize: 7,
          lineStyle: { color: r.color, width: 2 },
          itemStyle: { color: r.color },
          connectNulls: false,
        })),
    };
  }, [trendData, effectiveHidden, metricDef, resultSeries]);

  const visibleResultSeries = isBinary
    ? resultSeries.filter((r) => r.key !== 'goal')
    : resultSeries;

  const hasData = viewMode === 'groups' ? buckets.length > 0 : trendData.length > 0;
  const chartOption = viewMode === 'groups' ? groupsOption : trendsOption;

  return (
    <div className='pp-section'>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h4 style={{ margin: 0 }}>{viewMode === 'trends' ? t('playerPerformance.resultBreakdownTitle_trends') : t('playerPerformance.resultBreakdownTitle')} <ChartHint hintKey='playerPerformance.chartHint_results' /></h4>
        </div>
        <div className='pp-compare-mode-toggle' style={{ flexShrink: 0, marginLeft: 12 }}>
          <button
            type='button'
            className={`pp-compare-mode-btn${viewMode === 'groups' ? ' pp-compare-mode-btn--active' : ''}`}
            onClick={() => setViewMode('groups')}
          >
            {t('playerPerformance.resultBreakdown_modeGroups')}
          </button>
          <button
            type='button'
            className={`pp-compare-mode-btn${viewMode === 'trends' ? ' pp-compare-mode-btn--active' : ''}`}
            onClick={() => setViewMode('trends')}
          >
            {t('playerPerformance.resultBreakdown_modeTrends')}
          </button>
        </div>
      </div>

      {viewMode === 'trends' && (
        <div className='pp-trend-picker' style={{ marginBottom: 8 }}>
          {TREND_GROUP_OPTIONS.map(({ key, labelKey }) => (
            <button
              key={key}
              type='button'
              className={`pp-trend-pill${trendGroupBy === key ? ' pp-trend-pill--active' : ''}`}
              onClick={() => setTrendGroupBy(key)}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
      )}

      {!hasData ? (
        <p className='pp-hint' style={{ textAlign: 'center', padding: '20px 0' }}>
          {t('playerPerformance.resultBreakdown_noData')}
        </p>
      ) : (
        // The chart grows with its card (cells in a grid row share one height) but never drops below its base height.
        <GrowChart option={chartOption} notMerge />
      )}

      <div className='pp-scatter-legend'>
        {visibleResultSeries.map((r) => {
          const isHidden = hiddenResults.has(r.key);
          return (
            <button
              key={r.key}
              type='button'
              className={`pp-scatter-legend-item${isHidden ? ' pp-scatter-legend-item--hidden' : ''}`}
              onClick={() => toggleResult(r.key)}
            >
              <span
                className='pp-scatter-legend-dot'
                style={{ background: isHidden ? '#D1D5DB' : r.color }}
              />
              {t(r.labelKey)}
            </button>
          );
        })}
      </div>

      <div className='pp-metric-pills'>
        {metrics.map((m) => (
          <button
            key={m.key}
            type='button'
            className={`pp-trend-pill${metricDef.key === m.key ? ' pp-trend-pill--active' : ''}`}
            onClick={() => setSelectedMetric(m.key)}
          >
            {t(m.labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ResultBreakdownChart;
